/**
 * glør Warenkorb-Sets — gruppiert die Zeilen eines Bundles im Warenkorb.
 *
 * WAS DAS IST: reine ANZEIGE. Die Zeilen bleiben einzelne Positionen, Preise,
 * Mengen und der Checkout aendern sich nicht. Gruppiert wird anhand der
 * Eigenschaften, die die Bundle-Mechanik ohnehin an jede Zeile haengt:
 *
 *   _glr_bundle        gemeinsame Klammer aller Zeilen eines Sets
 *   _glr_bundle_role   "parent" (traegt den Titel) oder "child"
 *   _glr_bundle_title  Name des Sets, z. B. "Ritual Set"
 *
 * WAS DAS NICHT IST: Shopifys eigene Bundle-Verschachtelung
 * (`item.parent_relationship`). Die entsteht nur ueber eine Cart-Transform-
 * Function, also ueber eine App - und nur sie wirkt bis in den Checkout.
 * Hier geht es ausschliesslich um Warenkorbseite und Cart-Drawer.
 *
 * ZUORDNUNG ueber `data-key` am <tr> gegen `key` aus /cart.js. Bewusst nicht
 * ueber die Reihenfolge: die App legt die Zeilen in beliebiger Folge an - beim
 * Ritual Set steht das Hauptprodukt zuletzt.
 */
class GlrCartBundles extends HTMLElement {
  /** Ereignisse, nach denen sich der Inhalt geaendert haben kann. */
  static CHANGE_EVENTS = [
    'shopify:cart:lines-update',
    'shopify:cart:update',
    'cart:lines-update',
    'cart:update',
    'CartUpdateEvent',
    'cart:refresh',
  ];

  /** Ereignisse, die den Warenkorb nur anzeigen. */
  static VIEW_EVENTS = ['shopify:cart:view', 'cart:view'];

  connectedCallback() {
    this.titleWord = this.dataset.titleWord || '';
    this.partsWord = this.dataset.partsWord || '';
    this.hideChildPrice = this.dataset.hideChildPrice === 'true';
    // Global eingebunden: es gibt keine Warenkorb-Section, in die sich ein
    // Block legen liesse. Also wird das ganze Dokument betrachtet - der
    // Cart-Drawer haengt ohnehin ausserhalb der Seiten-Sections.
    this.scope = document;
    this.signature = '';
    this.busy = false;
    // Zwischenspeicher der Warenkorbdaten. Liegt er vor, kann die Gruppierung
    // ohne Netzwerkweg laufen - und zwar noch im selben Bild, in dem das Theme
    // die Zeilen einsetzt. Genau das verhindert das kurze Aufblitzen der
    // ungruppierten Liste.
    this.cache = null;
    this.warming = null;
    this.pending = false;
    this.warmedFor = null;

    this.#apply();

    // Nach jeder Warenkorb-Aenderung rendert das Theme die Zeilen neu.
    this.onCartUpdate = (event) => {
      // Nach einer echten Warenkorb-Aenderung ist der alte Fingerabdruck
      // wertlos - das Theme rendert die Zeilen neu.
      this.signature = '';
      this.cache = null;
      this.warmedFor = null;

      // Das Ereignis feuert, BEVOR die Aenderung durch ist - ein sofortiges
      // /cart.js liefert deshalb noch den alten Stand. Es traegt aber ein
      // Promise, das mit den fertigen Zeilen aufloest, Eigenschaften inklusive.
      // Daraus laesst sich der Zwischenspeicher ohne eine einzige zusaetzliche
      // Anfrage fuellen - und zwar in dem Moment, in dem das Theme die Zeilen
      // einsetzt. Genau das nimmt dem Umbau das Aufblitzen.
      const promise = event?.promise;
      if (promise?.then) {
        // Solange die Aenderung laeuft, keine eigenen Abrufe starten - die
        // Daten kommen mit dem Promise.
        this.pending = true;
        promise
          .then((result) => {
            this.pending = false;
            const items = result?.detail?.items;
            if (Array.isArray(items) && items.length) {
              this.cache = this.#build({ items });
              this.#applySoon();
            } else {
              this.#warm();
            }
          })
          .catch(() => {
            this.pending = false;
            this.#warm();
          });
      } else {
        this.#warm();
      }

      this.#applySoon();
    };
    // Horizon feuert `shopify:cart:lines-update`, sobald etwas in den Warenkorb
    // geht, und `shopify:cart:view`, wenn der Drawer aufgeht. Ohne diese beiden
    // Namen lief das Vorwaermen nie an und die Gruppierung startete erst, wenn
    // die Zeilen schon standen - genau das war das Aufblitzen.
    for (const name of GlrCartBundles.CHANGE_EVENTS) {
      document.addEventListener(name, this.onCartUpdate);
    }

    // Reines Ansehen aendert nichts am Inhalt: hier wird nur vorgewaermt.
    this.onCartView = () => {
      if (!this.cache) this.#warm();
      this.#applySoon();
    };
    for (const name of GlrCartBundles.VIEW_EVENTS) {
      document.addEventListener(name, this.onCartView);
    }

    // Der Drawer wird erst beim Oeffnen gefuellt, die Tabelle existiert beim
    // Laden also noch gar nicht. Deshalb am body beobachten - und in #apply()
    // sofort aussteigen, wenn keine Warenkorb-Tabelle da ist, damit daraus
    // keine Dauerabfrage wird.
    if ('MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.#applySoon());
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    for (const name of GlrCartBundles.CHANGE_EVENTS) {
      document.removeEventListener(name, this.onCartUpdate);
    }
    for (const name of GlrCartBundles.VIEW_EVENTS) {
      document.removeEventListener(name, this.onCartView);
    }
    this.observer?.disconnect();
  }

  #applySoon() {
    // Waehrend des eigenen Umbaus feuert der Beobachter fuer die eigenen
    // Aenderungen. Ohne diese Sperre ruft sich das Ganze endlos selbst auf.
    if (this.busy) return;
    // Liegen die Daten schon vor, wird sofort umgebaut - im Rueckruf des
    // Beobachters, also vor dem naechsten Bild. Nur wenn Zeilen auftauchen,
    // die der Zwischenspeicher nicht kennt, geht es ueber den langen Weg.
    if (this.#applyCached()) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.#apply(), 120);
  }

  /**
   * Laedt den Warenkorb und legt ihn in den Zwischenspeicher. Mehrfachaufrufe
   * teilen sich dieselbe Anfrage.
   * @returns {Promise<void>}
   */
  #warm() {
    if (this.warming) return this.warming;
    this.warming = fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        this.cache = this.#build(cart);
        this.warming = null;
        // Falls die Zeilen inzwischen dastehen, jetzt nachziehen.
        this.#applySoon();
      })
      .catch(() => {
        this.warming = null;
      });
    return this.warming;
  }

  /**
   * Baut aus den Warenkorbdaten die Gruppen und merkt sich alle Zeilen-Keys.
   * @param {{items?: Array<{key: string, properties?: Record<string, string>}>}} cart
   * @returns {{groups: Map<string, {title: string, parent: string|null, children: string[]}>, keys: Set<string>, variants: Map<string, string>, contents: Map<string, {keys: string[], list: string}>}}
   */
  #build(cart) {
    const groups = new Map();
    const keys = new Set();
    // Variantentitel je Zeile - fuer Shopify-Bundles, deren Variante das Theme
    // im Warenkorb ausblendet (siehe #labelBundles).
    const variants = new Map();
    // Lieferumfang je Zeile - fuer fertig gepackte Sets (normale Produkte),
    // die ihren Inhalt als Eigenschaft mitbringen (siehe #listContents).
    const contents = new Map();
    for (const item of cart?.items || []) {
      keys.add(item.key);
      if (item.variant_title) variants.set(item.key, item.variant_title);
      const props = item.properties || {};
      // _glr_contents: Name(n) der sichtbaren Inhalts-Eigenschaften, mit | getrennt.
      // _glr_contents_list: optional die Liste fuer den Warenkorb (A · B · C).
      // Fehlt sie, wird die (einzige) Eigenschaft selbst zur Liste.
      const contentKeys = String(props._glr_contents || '')
        .split('|')
        .map((s) => s.trim())
        .filter((k) => k && props[k]);
      if (contentKeys.length) {
        const list = props._glr_contents_list || props[contentKeys[0]];
        contents.set(item.key, { keys: contentKeys, list: String(list) });
      }
      const id = props._glr_bundle;
      if (!id) continue;
      if (!groups.has(id)) groups.set(id, { title: '', parent: null, children: [] });
      const group = groups.get(id);
      if (props._glr_bundle_title) group.title = props._glr_bundle_title;
      if (props._glr_bundle_role === 'parent') group.parent = item.key;
      else group.children.push(item.key);
    }
    return { groups, keys, variants, contents };
  }

  /**
   * Shopify-Bundles: Horizon zeigt bei einer Zeile mit Komponenten die
   * Variante NICHT mehr an (cart-products.liquid prueft
   * `item.item_components.size == 0`). Im Warenkorb stuende dann nur
   * "glør Set" samt Bestandteilen - ob Starter oder Ritual, saehe der Kunde
   * erst im Checkout, der die Variante sehr wohl zeigt. Diese Zeile holt
   * genau das nach, damit Warenkorb und Checkout dasselbe sagen.
   * Reine Anzeige: es wird nichts mitgeschickt und nichts gerechnet.
   * @param {Element} body
   */
  #labelBundles(body) {
    const titles = this.cache?.variants;
    if (!titles) return;
    for (const row of body.querySelectorAll('[data-key]')) {
      // Nur Zeilen mit Bundle-Komponenten, und nur solange das Theme die
      // Variante nicht ohnehin selbst zeigt.
      if (!row.querySelector('.cart-items__bundle')) continue;
      if (row.querySelector('.cart-items__variants')) continue;
      const title = titles.get(row.getAttribute('data-key'));
      if (!title) continue;
      const anchor = row.querySelector('.cart-items__title-row');
      if (!anchor) continue;
      let line = row.querySelector('.glr-set-variant');
      if (!line) {
        line = document.createElement('p');
        line.className = 'glr-set-variant';
        anchor.insertAdjacentElement('afterend', line);
      }
      if (line.textContent !== title) line.textContent = title;
    }
  }

  /**
   * Fertig gepackte Sets (z. B. das Ritual Set als normales Produkt) haben
   * keine Shopify-Bundle-Komponenten. Ihren Inhalt bringen sie als sichtbare
   * Eigenschaft mit - die braucht der Checkout. Im Warenkorb stuende sie als
   * „Label: A · B · C" in einer Zeile. Hier wird daraus dieselbe Liste wie bei
   * einem Shopify-Bundle (`.cart-items__bundle`), damit beides gleich aussieht.
   * Reine Anzeige: Eigenschaft, Preis und Checkout bleiben unveraendert.
   * @param {Element} body
   */
  #listContents(body) {
    const labels = this.cache?.contents;
    if (!labels?.size) return;
    for (const row of body.querySelectorAll('[data-key]')) {
      const entry = labels.get(row.getAttribute('data-key'));
      if (!entry || row.querySelector('.glr-set-contents')) continue;
      // Alle sichtbaren Inhalts-Eigenschaften dieser Zeile (im Checkout je eine
      // Zeile „Duftoel: …", „Garantie: …"). Im Warenkorb ersetzt sie die Liste.
      const properties = [...row.querySelectorAll('.cart-items__property')].filter((el) =>
        entry.keys.includes((el.querySelector('dt')?.textContent ?? '').trim().replace(/:$/, ''))
      );
      const parts = entry.list
        .split(' · ')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!properties.length || !parts.length) continue;
      const property = properties[0];
      for (const extra of properties.slice(1)) extra.remove();
      const list = document.createElement('ul');
      list.className = 'cart-items__bundle list-unstyled glr-set-contents';
      for (const part of parts) {
        const li = document.createElement('li');
        li.textContent = part;
        list.append(li);
      }
      // Die Liste ersetzt die Eigenschaft. Steht sonst keine sichtbare
      // Eigenschaft in der Liste, faellt die leere <dl> gleich mit weg.
      const dl = property.closest('dl');
      if (dl && dl.querySelectorAll('.cart-items__property').length === 1) {
        dl.replaceWith(list);
      } else {
        property.remove();
        (dl ?? row).after(list);
      }
    }
  }

  /**
   * Der schnelle Weg: gruppiert aus dem Zwischenspeicher, ohne zu warten.
   * @returns {boolean} true, wenn nichts mehr zu tun ist
   */
  #applyCached() {
    if (!this.cache) return false;
    const tables = [...document.querySelectorAll('.cart-items__table tbody')];
    if (!tables.length) {
      this.signature = '';
      return true;
    }
    const signature = this.#signature(tables);
    if (signature === this.signature) return true;

    // Steht eine Zeile da, die der Zwischenspeicher nicht kennt, sind die
    // Daten veraltet - dann lieber der lange Weg mit frischem /cart.js.
    for (const row of document.querySelectorAll('.cart-items__table tbody [data-key]')) {
      if (!this.cache.keys.has(row.getAttribute('data-key'))) return false;
    }

    this.signature = signature;
    this.#write(() => {
      if (this.#hasNativeNesting()) {
        for (const table of tables) this.#reset(table);
      } else if (this.cache.groups.size) {
        for (const table of tables) this.#layout(table, this.cache.groups);
      }
      // Laeuft in JEDEM Fall - ein Shopify-Bundle bringt keine eigenen
      // Gruppen-Properties mit, waere ueber die Zweige oben also nie erreicht.
      for (const table of tables) this.#labelBundles(table);
      for (const table of tables) this.#listContents(table);
    });
    this.signature = this.#signature(tables);
    return true;
  }

  /**
   * Fingerabdruck der Warenkorbzeilen: welche Zeilen stehen in welcher
   * Reihenfolge da und wie sind sie bereits einsortiert. Stimmt er mit dem
   * letzten Stand ueberein, ist nichts zu tun - und vor allem ist dann auch
   * kein /cart.js noetig.
   * @param {HTMLElement[]} tables
   * @returns {string}
   */
  #signature(tables) {
    return tables
      .map((body) =>
        [...body.children]
          .map((row) => {
            const id = row.getAttribute('data-key') || row.getAttribute('data-glr-set-head') || '?';
            const marks = (row.className.match(/glr-set-[\w-]+/g) || []).join('.');
            // Ob die Variantenzeile schon steht, gehoert in den Fingerabdruck:
            // nach einem Neu-Rendern des Drawers sind die Zeilen dieselben,
            // die Ergaenzung aber weg - ohne dieses Merkmal wuerde sie nie
            // wieder gesetzt.
            const vlabel = row.querySelector?.('.glr-set-variant') ? 'v' : '';
            // Dasselbe fuer die Inhaltsliste fertig gepackter Sets.
            const clist = row.querySelector?.('.glr-set-contents') ? 'c' : '';
            return `${id}:${marks}:${vlabel}${clist}`;
          })
          .join(',')
      )
      .join('|');
  }

  async #apply() {
    // Billiger Vorabtest: ohne Warenkorb-Tabelle gibt es nichts zu ordnen.
    const tables = [...document.querySelectorAll('.cart-items__table tbody')];
    if (!tables.length) {
      this.signature = '';
      return;
    }

    // Unveraendert seit dem letzten Durchlauf? Dann sofort raus.
    const signature = this.#signature(tables);
    if (signature === this.signature) return;

    // Sobald eine Cart-Transform-Function laeuft, liefert Shopify selbst eine
    // Parent/Child-Struktur, und das Theme rueckt sie von sich aus ein. Diese
    // Gruppierung haelt dann still, sonst gaebe es beides doppelt.
    if (this.#hasNativeNesting()) {
      this.signature = signature;
      this.#write(() => {
        for (const table of tables) this.#reset(table);
        for (const table of tables) this.#labelBundles(table);
      });
      this.signature = this.#signature(tables);
      return;
    }

    // Steht eine Aenderung noch aus, kommen die Daten gleich mit dem Ereignis -
    // dann jetzt nichts abrufen.
    if (this.pending) return;

    // Zweimal denselben Stand abrufen bringt nichts.
    if (this.warmedFor === signature) return;
    this.warmedFor = signature;

    await this.#warm();
    this.#applyCached();
  }


  /**
   * Klammert eigene DOM-Aenderungen ein: der Beobachter ruht solange, damit
   * der Umbau sich nicht selbst wieder anstoesst.
   * @param {() => void} fn
   */
  #write(fn) {
    this.busy = true;
    this.observer?.disconnect();
    try {
      fn();
    } finally {
      this.observer?.observe(document.body, { childList: true, subtree: true });
      // Erst nach dem Abarbeiten der eigenen Mutationen wieder freigeben.
      setTimeout(() => {
        this.busy = false;
      }, 0);
    }
  }

  /**
   * Erkennt Shopifys eigene Bundle-Verschachtelung. `data-parent-key` steht an
   * jeder Zeile, ist aber leer, solange es keine Function gibt.
   * @returns {boolean}
   */
  #hasNativeNesting() {
    if (document.querySelector('.cart-items__nested-line')) return true;
    for (const row of document.querySelectorAll('[data-parent-key]')) {
      if (row.getAttribute('data-parent-key')) return true;
    }
    return false;
  }

  /**
   * Nimmt einen frueheren Durchlauf zurueck.
   * @param {HTMLElement} body
   */
  #reset(body) {
    body.querySelectorAll('[data-glr-set-head]').forEach((el) => el.remove());
    body.querySelectorAll('.glr-set-parent, .glr-set-child, .glr-set-last').forEach((el) => {
      el.classList.remove('glr-set-parent', 'glr-set-child', 'glr-set-last');
    });
    body.classList.remove('glr-set-quiet');
  }

  /**
   * @param {HTMLElement} body
   * @param {Map<string, {title: string, parent: string|null, children: string[]}>} groups
   */
  #layout(body, groups) {
    this.#reset(body);

    for (const [id, group] of groups) {
      const parentRow = group.parent ? body.querySelector(`[data-key="${CSS.escape(group.parent)}"]`) : null;
      const childRows = group.children
        .map((key) => body.querySelector(`[data-key="${CSS.escape(key)}"]`))
        .filter((row) => row instanceof HTMLElement);
      if (!parentRow || !childRows.length) continue;

      // Ueberschrift des Sets ueber die volle Breite.
      const cols = parentRow.children.length || 1;
      const head = document.createElement('tr');
      head.setAttribute('data-glr-set-head', id);
      head.className = 'glr-set-head';
      const cell = document.createElement('td');
      cell.colSpan = cols;
      cell.className = 'glr-set-head__cell';

      const name = document.createElement('span');
      name.className = 'glr-set-head__name';
      name.textContent = [this.titleWord, group.title].filter(Boolean).join(' ');
      cell.append(name);

      // Teile-Anzahl: Hauptprodukt plus Zutaten. Sagt auf einen Blick, wie
      // weit das Set reicht - gerade wenn darunter weitere Positionen stehen.
      if (this.partsWord) {
        const count = document.createElement('span');
        count.className = 'glr-set-head__count';
        count.textContent = `${childRows.length + 1} ${this.partsWord}`;
        cell.append(count);
      }

      head.append(cell);

      parentRow.before(head);
      parentRow.classList.add('glr-set-parent');

      // Kinder direkt hinter das Hauptprodukt holen - unabhaengig davon, in
      // welcher Reihenfolge die App sie angelegt hat.
      let anchor = parentRow;
      for (const row of childRows) {
        row.classList.add('glr-set-child');
        anchor.after(row);
        anchor = row;
      }
      anchor.classList.add('glr-set-last');

      if (this.hideChildPrice) body.classList.add('glr-set-quiet');
    }
  }
}

if (!customElements.get('glr-cart-bundles')) {
  customElements.define('glr-cart-bundles', GlrCartBundles);
}
